import { ArchiveIcon, BoxesIcon, ContactIcon, MessageSquareIcon, TrendingUpIcon } from 'lucide-react'

import type { LucideIcon } from 'lucide-react'

/**
 * The five exports offered by the Exportación Avanzada tab.
 *
 * Titles and descriptions are kept identical to REPORT_TYPES in
 * `modules/reports/components/AdvancedReports.tsx`, and the preview columns
 * mirror the shapes returned by the API's reportsService, so the announcement
 * never promises a column the export does not contain.
 */
export interface AdvancedExportReport {
  id: string
  icon: LucideIcon
  title: string
  description: string
  /** Records shown in the simulated Vista Previa badge. */
  count: string
  /** Columns beyond the six the preview has room for. */
  extraColumns: number
  columns: string[]
  rows: string[][]
  /** Index of a column to emphasise, when one carries the point of the report. */
  highlightColumn?: number
}

export const ADVANCED_EXPORT_REPORTS: AdvancedExportReport[] = [
  {
    id: 'deals',
    icon: BoxesIcon,
    title: 'Inventario y Negocios',
    description: 'Datos completos de negocios, vehículos y vendedores.',
    count: '247',
    extraColumns: 14,
    columns: ['Patente', 'Marca', 'Modelo', 'Año', 'Estado Negocio', 'Vendedor'],
    rows: [
      ['JWKR·92', 'Toyota', 'RAV4', '2021', 'Negociando', 'C. Díaz'],
      ['PZBL·45', 'Chevrolet', 'Sail', '2019', 'Cerrado', 'L. Rojas'],
      ['HXTF·11', 'Hyundai', 'Tucson', '2022', 'Nuevo', 'A. Pérez'],
    ],
  },
  {
    id: 'clients',
    icon: ContactIcon,
    title: 'Directorio de Clientes',
    description: 'Lista limpia de clientes y datos de contacto.',
    count: '1.842',
    extraColumns: 5,
    columns: ['Email', 'Nombre', 'Apellido', 'RUT', 'Teléfono', 'Creación'],
    rows: [
      ['m.soto@mail.cl', 'Marcela', 'Soto', '15.428.903-4', '+56 9 8421 3390', '12-03-2026'],
      ['r.vega@mail.cl', 'Rodrigo', 'Vega', '9.774.201-8', '+56 9 7712 4408', '03-04-2026'],
      ['p.lagos@mail.cl', 'Paula', 'Lagos', '18.220.556-1', '+56 9 6620 1174', '27-05-2026'],
    ],
  },
  {
    id: 'pending-margins',
    icon: TrendingUpIcon,
    title: 'Margen Notas de Venta',
    description: 'Análisis financiero de notas de venta pendientes.',
    count: '38',
    extraColumns: 10,
    columns: ['Número', 'Cliente', 'Patente', 'Precio Venta', 'Otros Gastos', 'Margen Neto'],
    rows: [
      ['1042', 'Marcela Soto', 'JWKR·92', '$21.000.000', '$310.000', '$3.890.000'],
      ['1043', 'Rodrigo Vega', 'PZBL·45', '$7.800.000', '$145.000', '$1.650.000'],
      ['1044', 'Paula Lagos', 'HXTF·11', '$18.400.000', '$228.000', '$2.470.000'],
    ],
    highlightColumn: 5,
  },
  {
    id: 'messages',
    icon: MessageSquareIcon,
    title: 'Historial Comunicaciones',
    description: 'Registro de auditoría de mensajes por negocio.',
    count: '5.310',
    extraColumns: 2,
    columns: ['Fecha', 'Cliente', 'Patente', 'Emisor', 'Mensaje', 'Leído'],
    rows: [
      ['02-08-2026', 'Marcela Soto', 'JWKR·92', 'C. Díaz', 'Le confirmo la visita', 'Sí'],
      ['02-08-2026', 'Rodrigo Vega', 'PZBL·45', 'Sistema', 'Cotización enviada', 'No'],
      ['03-08-2026', 'Paula Lagos', 'HXTF·11', 'L. Rojas', '¿Sigue disponible?', 'Sí'],
    ],
  },
  {
    id: 'archived-deals',
    icon: ArchiveIcon,
    title: 'Negocios Cancelados',
    description: 'Análisis de negocios archivados y cancelados.',
    count: '64',
    extraColumns: 0,
    columns: ['Estado Final', 'Razón', 'Comentario', 'Cancelación', 'Marca', 'Modelo'],
    rows: [
      ['Archivado', 'Precio', 'Encontró mejor oferta', '18-06-2026', 'Kia', 'Sportage'],
      ['Cancelado', 'Financiamiento', 'Crédito rechazado', '24-06-2026', 'Nissan', 'Versa'],
      ['Archivado', 'Sin respuesta', '3 intentos de contacto', '09-07-2026', 'Suzuki', 'Swift'],
    ],
  },
]

export const EXCEL_COLUMN_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F']

/** The permission label exactly as it reads in the roles grid (locales/es/enums.json). */
export const ADVANCED_EXPORTS_ROLE_LABEL = 'Exportar reportes avanzados'
